/**
 * KI-Funktionen (Spec 9) — Schemas fuer Provider-Konfiguration, Entwuerfe,
 * Triage-Vorschlaege, Wissensbasis und Aehnlichkeitssuche.
 *
 * **Architektur-Invariante 3 („Human in the Loop") steckt im Datenmodell:**
 * ein `AiDraft` ist ein *Entwurf*, keine Nachricht. Er wird erst dann zur
 * Nachricht, wenn ein Agent ihn ueber den normalen Antwort-Endpunkt
 * (`POST /tickets/:id/messages` mit `ai_draft_id`) absendet — es gibt kein
 * Feld und keinen Status, der einen Versand ohne diesen Schritt ausdruecken
 * koennte. Dasselbe fuer Triage: ein `AiSuggestion` traegt einen *Vorschlag*,
 * nie den Wert selbst; das Ticket aendert sich erst beim Annehmen.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { tiptapDocSchema } from './richtext.js';
import { ticketPrioritySchema } from './ticket.js';
// ───────────────────────────────────────────── Provider (Spec 9.1) ──────────
/**
 * Die Adapter aus Spec 9.1 plus `mock` fuer Tests und Seeds. `mock` ist
 * deterministisch und ruft nie einen externen Dienst — damit laeuft die
 * komplette Strecke lokal ohne Schluessel und ohne Kosten.
 */
export const AI_PROVIDERS = ['mock', 'anthropic', 'openai'];
export const aiProviderSchema = z.enum(AI_PROVIDERS);
/** Default-Modelle je Provider. Anthropic: `claude-sonnet-5` (Vorgabe Phase 4). */
export const DEFAULT_AI_MODELS = {
    mock: 'mock-chat',
    anthropic: 'claude-sonnet-5',
    openai: 'gpt-5.4-mini',
};
/**
 * Default-Embedding-Modelle. **Anthropic bietet keine Embeddings** — ein
 * Mandant auf Anthropic braucht dafuer einen zusaetzlichen OpenAI-Schluessel
 * (`embedding_api_key`), sonst bleiben KB-Retrieval und „aehnliche Tickets"
 * aus (ADR 0009).
 */
export const DEFAULT_AI_EMBEDDING_MODELS = {
    mock: 'mock-embed',
    anthropic: 'text-embedding-3-small',
    openai: 'text-embedding-3-small',
};
/** Dimension der Embedding-Spalten (`tickets.embedding`, `kb_articles.embedding`). */
export const AI_EMBEDDING_DIMENSIONS = 1536;
// ─────────────────────────────────── Mandanten-Konfiguration (Spec 9.1) ─────
export const DEFAULT_AI_DAILY_CALL_LIMIT = 200;
export const DEFAULT_AI_DAILY_TOKEN_LIMIT = 500_000;
/** Tageszaehler je Mandant (Kosten-Kontrolle, `background-jobs.md`). */
export const aiUsageSchema = z.object({
    /** ISO-Datum (UTC) des Zaehlers. */
    day: z.string(),
    calls: z.number().int().nonnegative(),
    tokens: z.number().int().nonnegative(),
});
/**
 * KI-Einstellungen, wie die API sie ausliefert.
 *
 * **Seit ADR 0017 stehen hier keine Schluessel mehr.** Provider, Modell und
 * die beiden Schluessel liegen in einer `ai_provider`-Verbindung aus dem
 * Vault; der Mandant waehlt nur noch *welche*. `provider`, `model` und
 * `embedding_model` sind deshalb **abgeleitet** und nur Anzeige — ohne
 * gewaehlte Verbindung gilt der Mock (kein Aufruf nach draussen, keine
 * Kosten).
 *
 * Was dem Mandanten bleibt, sind die Entscheidungen, die ihm gehoeren: der
 * An/Aus-Schalter (Spec 3.2, DSGVO) und die Tageslimits.
 */
export const aiSettingsSchema = z.object({
    /** Spiegelt `tenants.settings.ai_enabled` (Spec 3.2, DSGVO: abschaltbar). */
    enabled: z.boolean(),
    /** `null` = keine Verbindung gewaehlt, es gilt der Mock-Adapter. */
    connection_id: uuidSchema.nullable(),
    connection_name: z.string().nullable(),
    provider: aiProviderSchema,
    model: z.string().min(1).max(200),
    embedding_model: z.string().min(1).max(200),
    daily_call_limit: z.number().int().min(0).max(100_000),
    daily_token_limit: z.number().int().min(0).max(100_000_000),
    /** Heutiger Verbrauch — die Grundlage der Anzeige im Admin-UI. */
    usage: aiUsageSchema,
    /** `false`, wenn kein Embedding-Weg konfiguriert ist (siehe ADR 0009). */
    embeddings_available: z.boolean(),
});
/**
 * Aenderungen an den KI-Einstellungen. Ein weggelassenes Feld bleibt
 * unveraendert; `connection_id: null` nimmt die Wahl zurueck.
 */
export const updateAiSettingsSchema = z.object({
    enabled: z.boolean().optional(),
    connection_id: uuidSchema.nullable().optional(),
    daily_call_limit: z.number().int().min(0).max(100_000).optional(),
    daily_token_limit: z.number().int().min(0).max(100_000_000).optional(),
});
// ────────────────────────────────────── Antwort-Entwuerfe (Spec 9.2) ────────
/**
 * Status eines Entwurfs (Spec 3.2). Alle vier beschreiben eine **Agent-
 * Entscheidung**, nicht den Job:
 *   - `proposed`  — die KI hat geliefert, niemand hat ihn angefasst
 *   - `edited`    — ein Agent hat ihn in den Editor uebernommen
 *   - `sent`      — er ist als Nachricht rausgegangen (ueber den Antwort-Flow)
 *   - `discarded` — ein Agent hat ihn verworfen
 *
 * Der *Ladezustand* steht daneben in `generated_at`/`error` (ADR 0008) —
 * damit bleibt dieses Enum genau das, was Spec 3.2 vorgibt.
 */
export const AI_DRAFT_STATUSES = ['proposed', 'edited', 'sent', 'discarded'];
export const aiDraftStatusSchema = z.enum(AI_DRAFT_STATUSES);
/**
 * Woher ein Zitat stammt (Spec 9.2: Quellenliste unter dem Entwurf).
 * `wiki_article` kommt aus der SkillLock-Live-Suche (Spec 9.4, Phase 5) —
 * ein Treffer ohne lokale Zeile.
 */
export const AI_CITATION_KINDS = ['kb_article', 'ticket', 'wiki_article'];
export const aiCitationKindSchema = z.enum(AI_CITATION_KINDS);
export const aiCitationSchema = z.object({
    kind: aiCitationKindSchema,
    /**
     * UUID bei `kb_article`/`ticket`, die Wiki-ID der Gegenseite bei
     * `wiki_article` — deshalb `string` und nicht `uuid`.
     */
    id: z.string().min(1).max(200),
    title: z.string(),
    /** Ticketnummer bzw. Kurzhinweis auf die Herkunft. */
    reference: z.string().nullable().default(null),
    /** Cosine-Aehnlichkeit, auf drei Stellen gerundet. */
    score: z.number().nullable().default(null),
});
/**
 * Was in den Prompt geflossen ist (Spec 3.2: „welche KB-Artikel/Tickets
 * herangezogen wurden"). Nachvollziehbarkeit ist hier der Zweck — das Feld
 * wird nie fuer die Anzeige gebraucht, sondern fuer die Frage „warum stand
 * das im Entwurf?".
 */
export const aiPromptContextSchema = z.object({
    message_count: z.number().int().nonnegative(),
    kb_article_ids: z.array(uuidSchema),
    similar_ticket_ids: z.array(uuidSchema),
    /** IDs der Treffer aus der SkillLock-Wiki-Live-Suche (Spec 9.4, Phase 5). */
    wiki_hit_ids: z.array(z.string()).default([]),
    metadata_keys: z.array(z.string()),
    /** `false` = ohne Embeddings gebaut (kein Retrieval, nur Verlauf). */
    embeddings_used: z.boolean(),
});
export const aiDraftSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    requested_by_member_id: uuidSchema.nullable(),
    provider: aiProviderSchema,
    model: z.string(),
    prompt_context: aiPromptContextSchema,
    draft_json: tiptapDocSchema,
    citations: z.array(aiCitationSchema),
    status: aiDraftStatusSchema,
    /** `null` = der Job laeuft noch (ADR 0008). */
    generated_at: z.iso.datetime().nullable(),
    /** Gesetzt, wenn die Erzeugung fehlgeschlagen ist — Meldung fuer das UI. */
    error: z.string().nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/**
 * Was ein Agent mit einem Entwurf machen darf, **ohne** ihn zu versenden.
 * `sent` fehlt hier bewusst: dieser Status entsteht ausschliesslich im
 * Antwort-Flow (Invariante 3).
 */
export const updateAiDraftSchema = z.object({
    status: z.enum(['edited', 'discarded']),
});
// ───────────────────────────────────────── Auto-Triage (Spec 9.3) ───────────
/** Felder, zu denen die KI einen Vorschlag machen darf. */
export const AI_SUGGESTION_FIELDS = ['priority', 'tags', 'queue'];
export const aiSuggestionFieldSchema = z.enum(AI_SUGGESTION_FIELDS);
export const AI_SUGGESTION_STATUSES = ['pending', 'accepted', 'rejected'];
export const aiSuggestionStatusSchema = z.enum(AI_SUGGESTION_STATUSES);
/**
 * Der vorgeschlagene Wert. Je Feld genau eine Form — als diskriminierte
 * Union, damit das UI nicht raten muss.
 */
export const aiSuggestionValueSchema = z.discriminatedUnion('field', [
    z.object({ field: z.literal('priority'), priority: ticketPrioritySchema }),
    z.object({ field: z.literal('tags'), tag_ids: z.array(uuidSchema).max(10) }),
    z.object({ field: z.literal('queue'), queue_id: uuidSchema }),
]);
export const aiSuggestionSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    field: aiSuggestionFieldSchema,
    value: aiSuggestionValueSchema,
    /** Ein Satz Begruendung aus dem Modell — im UI unter dem Vorschlag. */
    reason: z.string(),
    provider: aiProviderSchema,
    model: z.string(),
    status: aiSuggestionStatusSchema,
    decided_by_member_id: uuidSchema.nullable(),
    decided_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime(),
});
// ───────────────────────────────────────── Wissensbasis (Spec 9.4) ──────────
/**
 * Herkunft eines Artikels (Spec 3.2). `skilllock_wiki` ist **vorbereitet**,
 * aber ohne Sync — die Live-Suche gegen SkillLock kommt in Phase 5 (Spec 9.4).
 */
export const KB_ARTICLE_ORIGINS = ['skilllock_wiki', 'curated_ticket', 'manual'];
export const kbArticleOriginSchema = z.enum(KB_ARTICLE_ORIGINS);
export const kbArticleSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    origin: kbArticleOriginSchema,
    external_ref: z.string().nullable(),
    title: z.string().min(1).max(300),
    body_text: z.string(),
    active: z.boolean(),
    /** `true`, sobald das Embedding berechnet ist — sonst faellt der Artikel aus dem Retrieval. */
    has_embedding: z.boolean(),
    /** Gesetzt bei `origin = 'curated_ticket'` (Spec 9.4). */
    source_ticket_id: uuidSchema.nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createKbArticleSchema = z.object({
    title: z.string().min(1).max(300),
    body_text: z.string().min(1).max(50_000),
    /** Der Sync-Origin `skilllock_wiki` wird nicht von Hand vergeben. */
    origin: z.enum(['manual', 'curated_ticket']).default('manual'),
    source_ticket_id: uuidSchema.nullable().default(null),
    active: z.boolean().default(true),
});
export const updateKbArticleSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    body_text: z.string().min(1).max(50_000).optional(),
    active: z.boolean().optional(),
});
/** Ergebnis von „aus Ticket kuratieren" (Spec 9.4) — noch kein Artikel. */
export const kbArticleDraftSchema = z.object({
    title: z.string(),
    body_text: z.string(),
    source_ticket_id: uuidSchema,
});
// ──────────────────────────────────── Aehnliche Tickets (Spec 9.5) ──────────
/**
 * Ab dieser Cosine-Aehnlichkeit gilt ein Treffer als Duplikat-Kandidat
 * (Spec 9.3: „Duplikat-Hinweis via Embedding-Aehnlichkeit").
 *
 * Der Wert ist bewusst hoch: ein falscher Duplikat-Hinweis kostet einen Agent
 * mehr Zeit, als ein fehlender ihm spart. Der Hinweis ist ausserdem nie eine
 * Aktion, sondern ein Vorschlag mit „Zusammenführen"-Knopf daneben (Spec 7.2).
 */
export const DUPLICATE_SIMILARITY_THRESHOLD = 0.9;
export const similarTicketSchema = z.object({
    ticket_id: uuidSchema,
    number: z.string(),
    subject: z.string(),
    status: z.string(),
    /** Cosine-Aehnlichkeit 0…1, absteigend sortiert. */
    score: z.number(),
    /** `score >= DUPLICATE_SIMILARITY_THRESHOLD` — der Hinweis aus Spec 9.3. */
    duplicate_candidate: z.boolean().default(false),
    created_at: z.iso.datetime(),
});
//# sourceMappingURL=ai.js.map